import { useContext, useEffect, useEffectEvent, useState } from 'react'
import { useParams } from 'react-router'
import { Box, Skeleton } from '@mui/material'
import { useSnackbar } from 'notistack'

import BreadcrumbsComponent from '../../components/breadcrumbs'
import OrderDetails from '../../components/orders/order-details'
import { AppContext, useHost } from '../../providers/app-provider'
import { useAuthentication } from '../../providers/auth-provider'
import Api from '../../utils/api'

const Orders = () => {
  const { orderId } = useParams()

  const { enqueueSnackbar } = useSnackbar()
  const { auth } = useAuthentication()
  const host = useHost()
  const { pageRefreshCounter } = useContext(AppContext)

  const [orderNode, setOrderNode] = useState<INodeType>()
  const [order, setOrder] = useState<IOrderDetail | IReturnDetail | IReorderDetail>()
  const [folder, setFolder] = useState<IFolderType>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { folderId } = orderNode || {}

  const fetchOrder = async () => {
    if (!orderId || !host || !auth.user?.access_token) return
    setIsLoading(true)
    const resNode = await Api.get(`${host}/api/nodes/${orderId}`)
    if (resNode.ok) {
      const { folderId: nodeFolderId, nodeTypeName } = resNode.body
      setOrderNode(resNode.body)
      const resFolder = await Api.get(`${host}/api/folders/${nodeFolderId}`)
      if (resFolder.ok) {
        setFolder(resFolder.body)
      }
      const res = await Api.get(
        `${host}/api/v2/central/folders/${nodeFolderId}/${nodeTypeName === 'return' || nodeTypeName === 'scrap' ? 'returns' : nodeTypeName === 'reorder' ? 'reorders' : 'orders'}/${orderId}/details`
      )
      if (res.ok) {
        setOrder(res.body)
      } else {
        enqueueSnackbar('Something went wrong and we could not load the order', { variant: 'error' })
      }
    } else {
      enqueueSnackbar('Something went wrong and we could not load the order node', { variant: 'error' })
    }
    setIsLoading(false)
  }

  const fetchInitialOrder = useEffectEvent(fetchOrder)
  useEffect(() => {
    // Reset order details when orderId changes to prevent showing stale data while loading new data
    setOrderNode(undefined)
    setOrder(undefined)
    setFolder(undefined)

    if (orderId) fetchInitialOrder()
  }, [orderId, host, pageRefreshCounter])

  return (
    <div>
      <BreadcrumbsComponent folderId={folderId} />
      {isLoading ? (
        <>
          <Skeleton variant="text" height={48} sx={{ marginTop: 3 }} />
          <Skeleton variant="text" height={44} sx={{ marginTop: 1 }} />
          <Skeleton variant="rounded" height={316} sx={{ marginTop: 6, marginBottom: 2 }} />
        </>
      ) : order && orderNode && folderId ? (
        <Box sx={{ marginX: -3 }}>
          <OrderDetails
            folderId={folderId}
            folderName={folder?.name || ''}
            accountNumber=""
            orderType={orderNode.nodeTypeName as OrderType}
            orderDetail={order}
          />
        </Box>
      ) : null}
    </div>
  )
}

export default Orders
